import React, { useState, useEffect } from 'react';
import Input from '../../../Components/Input/Input';
import Button from '../../../Components/Button/Button';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { putApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    virtualNumber: '',
    llcName: '',
  });
  const [originalData, setOriginalData] = useState({ ...formData });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const fileInputRef = React.useRef(null);
  // Business/LLC logo
  const [llcLogo, setLlcLogo] = useState(null); // File picked for upload
  const [llcLogoPreview, setLlcLogoPreview] = useState(null);
  const [originalLlcLogo, setOriginalLlcLogo] = useState(null);
  const [isLlcLogoRemoved, setIsLlcLogoRemoved] = useState(false);

  // Load user data from localStorage on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          const userDetails = userData?.user_details || {};
          
          // Extract country code and phone number
          const countryCode = userDetails?.country_code || '+1';
          const phoneNumber = userDetails?.phone_number || '';
          
          // Combine country code and phone number for PhoneInput
          // PhoneInput expects format: countryCode + phoneNumber (without +)
          const fullPhoneNumber = countryCode && phoneNumber 
            ? `${countryCode.replace('+', '')}${phoneNumber}` 
            : '';

          const virtualNumberRaw = userDetails?.inbound_twilio_inquiry_number || userData?.inbound_twilio_inquiry_number || '';
          const fullVirtualNumber = virtualNumberRaw
            ? virtualNumberRaw.replace(/\D/g, '')
            : '';
          
          const initialData = {
            firstName: userDetails?.first_name || '',
            lastName: userDetails?.last_name || '',
            email: userDetails?.email || '',
            phoneNumber: fullPhoneNumber,
            virtualNumber: fullVirtualNumber,
            llcName: userDetails?.llc_name || '',
          };
          
          setFormData(initialData);
          setOriginalData(initialData);
          
          // Load profile image if available
          const profileImageUrl = userDetails?.profile_image || userDetails?.image || null;
          if (profileImageUrl) {
            setProfileImagePreview(profileImageUrl);
            setOriginalImage(profileImageUrl);
          }

          const llcLogoUrl = userDetails?.llc_logo || null;
          if (llcLogoUrl) {
            setLlcLogoPreview(llcLogoUrl);
            setOriginalLlcLogo(llcLogoUrl);
          }
        }
      } catch (error) {
        console.error('Error loading user data from localStorage:', error);
      }
    };

    loadUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
  };

  const handleEdit = () => {
    setOriginalData({ ...formData });
    setOriginalImage(profileImagePreview);
    setOriginalLlcLogo(llcLogoPreview);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setProfileImage(null);
    setProfileImagePreview(originalImage);
    setLlcLogo(null);
    setLlcLogoPreview(originalLlcLogo);
    setIsLlcLogoRemoved(false);
    setIsEditing(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (jpeg, jpg, png, gif, webp)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLlcLogoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset input to allow selecting the same file again
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} is not an image file`);
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo size should be less than 3MB');
      return;
    }

    setLlcLogo(file);
    setIsLlcLogoRemoved(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLlcLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLlcLogo = () => {
    setLlcLogo(null);
    setLlcLogoPreview(null);
    if (originalLlcLogo) {
      setIsLlcLogoRemoved(true);
    }
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // Extract country code and phone number from PhoneInput value
      // PhoneInput value format: countryCode + phoneNumber (e.g., "13123123123")
      const cleanedPhone = (formData.phoneNumber || '').replace(/\s/g, '');
      const digitsOnly = cleanedPhone.replace(/\D/g, '');
      const countryCodeMatch = digitsOnly.match(/^\d{1,4}/);
      const numericCountryCode = countryCodeMatch ? countryCodeMatch[0] : '';
      const phoneNumberDigits = numericCountryCode
        ? digitsOnly.slice(numericCountryCode.length)
        : digitsOnly;

      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();
      
      // Add fields (all optional, but at least one must be provided)
      if (formData.firstName) {
        formDataToSend.append('firstName', formData.firstName);
      }
      if (formData.lastName) {
        formDataToSend.append('lastName', formData.lastName);
      }
      if (phoneNumberDigits) {
        formDataToSend.append('phoneNumber', phoneNumberDigits);
      }
      if (numericCountryCode) {
        formDataToSend.append('countryCode', `+${numericCountryCode}`);
      }
      formDataToSend.append('llc_name', formData.llcName.trim());

      // Business/LLC logo: new file, or empty value when the existing one was removed
      if (llcLogo) {
        formDataToSend.append('llc_logo', llcLogo);
      } else if (isLlcLogoRemoved) {
        formDataToSend.append('llc_logo', '');
      }
      
      // Add profile image if selected
      if (profileImage) {
        formDataToSend.append('image', profileImage);
      }

      // Call API with multipart/form-data
      const response = await putApi('/auth/update-profile', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Profile updated successfully');
        
        try {
          // Get current userData from localStorage
          const userDataString = localStorage.getItem('userData');
          let userData = userDataString ? JSON.parse(userDataString) : {};
          
          // Extract profile image from response (check multiple possible locations)
          const responseImageUrl = response?.user_details?.profile_image || 
                                   response?.data?.user_details?.profile_image || 
                                   response?.user_details?.image || 
                                   response?.data?.user_details?.image ||
                                   response?.profile_image ||
                                   response?.data?.profile_image ||
                                   response?.image ||
                                   response?.data?.image;

          const responseDetails = response?.user_details || response?.data?.user_details || {};
          const responseLlcLogo = responseDetails.llc_logo;
          const updatedLlcLogo = responseLlcLogo !== undefined
            ? responseLlcLogo
            : (isLlcLogoRemoved ? null : userData.user_details?.llc_logo);
          
          // Update user_details with response data (prioritize response, then formData)
          const updatedUserDetails = {
            ...userData.user_details,
            ...(response?.user_details || response?.data?.user_details || {}),
            // Use formData values if response doesn't have them
            first_name: response?.user_details?.first_name || response?.data?.user_details?.first_name || formData.firstName,
            last_name: response?.user_details?.last_name || response?.data?.user_details?.last_name || formData.lastName,
            email: response?.user_details?.email || response?.data?.user_details?.email || formData.email,
            phone_number: phoneNumberDigits || userData.user_details?.phone_number,
            country_code: numericCountryCode ? `+${numericCountryCode}` : userData.user_details?.country_code,
            // Update profile image - prioritize response, then keep existing
            profile_image: responseImageUrl || userData.user_details?.profile_image || userData.user_details?.image,
            image: responseImageUrl || userData.user_details?.image || userData.user_details?.profile_image,
            llc_name: responseDetails.llc_name ?? formData.llcName.trim(),
            llc_logo: updatedLlcLogo || null,
          };

          // Sync Business/LLC logo preview with the saved value
          const newLlcLogoPreview = updatedLlcLogo || (llcLogo ? llcLogoPreview : null);
          setLlcLogoPreview(newLlcLogoPreview);
          setOriginalLlcLogo(newLlcLogoPreview);
          setLlcLogo(null);
          setIsLlcLogoRemoved(false);
          
          // Update image preview if new image was uploaded or response contains image
          if (profileImage || responseImageUrl) {
            const newImageUrl = responseImageUrl || profileImagePreview;
            if (newImageUrl) {
              setProfileImagePreview(newImageUrl);
              setOriginalImage(newImageUrl);
              setProfileImage(null);
            }
          }
          
          // Update userData in localStorage - ensure profile_image is saved at both levels if needed
          userData.user_details = updatedUserDetails;
          // Also update root level profile_image if it exists in the structure
          if (responseImageUrl) {
            userData.profile_image = responseImageUrl;
          }
          localStorage.setItem('userData', JSON.stringify(userData));
          
          // Update userName in localStorage for header (combine first_name and last_name)
          const fullName = `${updatedUserDetails.first_name || ''} ${updatedUserDetails.last_name || ''}`.trim();
          if (fullName) {
            localStorage.setItem('userName', fullName);
          }
          
          // Update form data with the latest values
          const countryCode = updatedUserDetails.country_code || '+1';
          const phoneNumber = updatedUserDetails.phone_number || '';
          const fullPhoneNumber = countryCode && phoneNumber 
            ? `${countryCode.replace('+', '')}${phoneNumber}` 
            : formData.phoneNumber;
          
          const updatedData = {
            firstName: updatedUserDetails.first_name || formData.firstName,
            lastName: updatedUserDetails.last_name || formData.lastName,
            email: updatedUserDetails.email || formData.email,
            phoneNumber: fullPhoneNumber,
            virtualNumber: formData.virtualNumber,
            llcName: updatedUserDetails.llc_name || '',
          };
          
          setFormData(updatedData);
          setOriginalData(updatedData);
          
          // Trigger a custom event to notify layouts to update
          window.dispatchEvent(new CustomEvent('userDataUpdated'));
        } catch (error) {
          console.error('Error updating localStorage:', error);
        }
        
        setIsEditing(false);
      } else {
        const errorMessage = response?.message || 'Failed to update profile. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update profile. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-profile-card">
      <div className="profile-header">
        <div className="profile-avatar-section">
          <div className="profile-avatar-container">
            <div className="profile-avatar">
              {profileImagePreview ? (
                <img 
                  src={profileImagePreview} 
                  alt="Profile" 
                  className="profile-avatar-image"
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  {formData.firstName?.[0]?.toUpperCase() || ''}{formData.lastName?.[0]?.toUpperCase() || ''}
                </div>
              )}
              {isEditing && (
                <div className="profile-avatar-camera-overlay" onClick={handleCameraClick}>
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className="profile-camera-icon"
                  >
                    <path 
                      d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <circle 
                      cx="12" 
                      cy="13" 
                      r="4" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        <div className="profile-actions">
          {!isEditing ? (
            <button className="profile-edit-button" onClick={handleEdit}>
              <img 
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/EditIcon.svg") : "/assets/images/EditIcon.svg"} 
                alt="Edit" 
                className="profile-edit-icon"
              />
            </button>
          ) : (
            <div className="profile-action-buttons">
              <Button
                className="btn-secondary profile-cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                className="btn-success profile-save-button"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-form">
        <div className="profile-form-row">
          <div className="profile-form-column">
            <Input
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              type="text"
              disabled={!isEditing}
              readOnly={!isEditing}
            />
          </div>
          <div className="profile-form-column">
            <Input
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              type="text"
              disabled={!isEditing}
              readOnly={!isEditing}
            />
          </div>
        </div>
        <div className="profile-form-row">
          <div className="profile-form-column">
            <Input
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              type="email"
              disabled={true}
              readOnly={true}
            />
          </div>
          <div className="profile-form-column">
            <div className="signup-phone-field">
              <label className="signup-phone-label">
                Phone
              </label>
              <PhoneInput
                country={'us'}
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                countryCodeEditable={false}
                inputClass="signup-phone-input"
                containerClass="signup-phone-input-container"
                buttonClass="signup-phone-country-button"
                dropdownClass="signup-phone-country-dropdown"
                enableSearch
                searchPlaceholder="Search country"
                placeholder="Enter phone number"
                disabled={!isEditing}
                inputProps={{
                  name: 'phoneNumber',
                  readOnly: !isEditing,
                }}
              />
            </div>
          </div>
        </div>
        <div className="profile-form-row">
          <div className="profile-form-column">
            <div className="signup-phone-field signup-phone-field-readonly">
              <label className="signup-phone-label">
                Virtual Number
              </label>
              <PhoneInput
                country={'us'}
                value={formData.virtualNumber}
                onChange={() => {}}
                countryCodeEditable={false}
                inputClass="signup-phone-input"
                containerClass="signup-phone-input-container"
                buttonClass="signup-phone-country-button"
                dropdownClass="signup-phone-country-dropdown"
                enableSearch={false}
                placeholder="Virtual number"
                disabled={true}
                inputProps={{
                  name: 'virtualNumber',
                  readOnly: true,
                }}
              />
            </div>
          </div>
          <div className="profile-form-column">
            <Input
              label="Business/LLC Name"
              name="llcName"
              value={formData.llcName}
              onChange={handleChange}
              type="text"
              placeholder="Enter business/LLC name"
              disabled={!isEditing}
              readOnly={!isEditing}
            />
          </div>
        </div>
        <div className="profile-form-row">
          <div className="profile-form-column">
            <div className="property-llc-logo-block">
              <span className="property-llc-logo-label">Logo</span>
              <div className="property-images-container">
                {llcLogoPreview ? (
                  <div className="property-image-preview-item">
                    <img src={llcLogoPreview} alt="Business/LLC logo" />
                    {isEditing && (
                      <button
                        type="button"
                        className="property-image-remove"
                        onClick={handleRemoveLlcLogo}
                        aria-label="Remove logo"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ) : isEditing ? (
                  <div className="property-images-upload-area">
                    <input
                      type="file"
                      id="llc-logo-upload"
                      accept="image/*"
                      onChange={handleLlcLogoChange}
                      className="property-images-input"
                    />
                    <label htmlFor="llc-logo-upload" className="property-images-upload-label">
                      <div className="property-images-upload-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="property-images-upload-text">Upload Logo</span>
                    </label>
                  </div>
                ) : (
                  <span className="property-llc-logo-label">No logo uploaded</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
